import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Check } from "lucide-react";
import { Flame, Link2, Share2, LayoutGrid, QrCode, ArrowRight, Store, Tag, MousePointerClick } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1669158197015-e3590ae59137?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBzaG9wJTIwZGFya3xlbnwwfHx8fDE3ODUwNjAxNDh8MA&ixlib=rb-4.1.0&q=85";
const CAFE = "https://images.unsplash.com/photo-1759282336232-140500b6a877?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxsb2NhbCUyMGNhZmUlMjBib3V0aXF1ZSUyMGRlYWxzJTIwZGlzY291bnR8ZW58MHx8fHwxNzg1MDYwMjM4fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: LayoutGrid, title: "One place for everything", desc: "List all your offers, deals and discounts in a single organized hub." },
  { icon: Share2, title: "Instant shareable page", desc: "We auto-generate a beautiful public page you can market anywhere." },
  { icon: MousePointerClick, title: "Everything clickable", desc: "Customers tap any deal and jump straight to the item or your link." },
  { icon: QrCode, title: "Simple payments", desc: "Scan a QR, pay, and our back office verifies it. No card setup." },
];

const MODULE_LABELS = {
  custom_domain: "Your own custom domain", themes: "Premium page themes",
  analytics: "Click analytics", unlimited_items: "Unlimited offers & categories",
};
const FAQ = [
  ["Can customers pre-order food?", "Yes. Choose Food, café or canteen when you sign up. Students add items to a cart, place a pre-order and get a token number, you see it on a live order board, and they collect it when it's ready."],
  ["How does payment work?", "Pay by bank transfer, JazzCash or Easypaisa, then submit your payment reference in the dashboard. We verify it and activate your plan — usually within a day."],
  ["What happens after the free trial?", "Your page stays online with the free limits. Upgrade any time to unlock unlimited offers, themes, analytics and custom domains."],
  ["Can customers order on WhatsApp?", "Yes. Every offer has an Order on WhatsApp button that opens a chat with your number and a ready message."],
  ["Can I use my own domain?", "Yes, on plans that include it. Add a DNS record, verify, and your page is served from your domain."],
];

function Pricing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);
  if (!plans.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-5 pb-24" id="pricing" data-testid="pricing-section">
      <div className="text-center"><span className="overline text-primary">Pricing</span>
        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">Simple pricing in PKR</h2>
        <p className="text-muted-foreground mt-2 text-sm">Start with a free trial. No card needed.</p></div>
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {plans.map((p) => (
          <div key={p.id} className="bg-card border border-white/10 rounded-2xl p-7">
            <h3 className="font-display text-xl font-semibold">{p.name}</h3>
            <div className="mt-3"><span className="font-display text-4xl font-bold">Rs {Number(p.price).toLocaleString()}</span><span className="text-muted-foreground text-sm">{p.interval === "one_time" ? " one-time" : `/${p.interval}`}</span></div>
            <ul className="mt-5 space-y-2 text-sm">
              {(p.modules || []).map((m) => <li key={m} className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" />{MODULE_LABELS[m] || m}</li>)}
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" />Public shareable page + WhatsApp ordering</li>
            </ul>
            <Link to="/register"><Button className="mt-6 w-full rounded-full bg-primary hover:bg-secondary">Start free trial</Button></Link>
          </div>
        ))}
      </div>
      <div className="mt-16 max-w-2xl mx-auto space-y-3" data-testid="faq">
        <h3 className="font-display text-xl font-bold text-center mb-4">Frequently asked questions</h3>
        {FAQ.map(([q, a]) => (
          <details key={q} className="bg-card border border-white/10 rounded-xl p-4 group">
            <summary className="cursor-pointer font-medium text-sm">{q}</summary>
            <p className="text-sm text-muted-foreground mt-2">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="landing-logo">
            <span className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Flame className="w-4 h-4 text-white" />
            </span>
            <span className="font-display text-lg font-bold tracking-tight">DealWisp</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#pricing" className="hidden sm:block text-sm text-muted-foreground hover:text-white px-2">Pricing</a>
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn" className="text-muted-foreground hover:text-white">Log in</Button></Link>
            <Link to="/register"><Button data-testid="landing-signup-btn" className="rounded-full bg-secondary hover:bg-primary">Start free trial</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="lg:col-span-6"
        >
          <span className="overline text-primary">For small businesses</span>
          <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            All your deals.<br /> One <span className="text-primary">shareable</span> page.
          </h1>
          <p className="mt-6 text-muted-foreground text-base max-w-md">
            Register your shop, list every offer and discount you run, and DealWisp builds a clickable marketing page for you — ready to share with customers in seconds.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register"><Button size="lg" className="rounded-full bg-primary hover:bg-secondary" data-testid="hero-cta-btn">
              Create your page <ArrowRight className="w-4 h-4 ml-1" />
            </Button></Link>
            <Link to="/b/demo"><Button size="lg" variant="outline" className="rounded-full border-white/15 hover:bg-white/5" data-testid="hero-demo-btn">
              See an example
            </Button></Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
            <div><span className="text-white font-display text-2xl font-bold">14</span> day free trial</div>
            <div className="h-8 w-px bg-white/10" />
            <div><span className="text-white font-display text-2xl font-bold">0</span> setup fees</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/10">
            <img src={HERO} alt="Small business" className="w-full h-[440px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 glass rounded-xl border border-white/10 p-4 flex items-center gap-3">
              <Link2 className="w-5 h-5 text-primary" />
              <span className="text-sm">dealwisp.online/b/<span className="text-primary">your-shop</span></span>
              <span className="ml-auto text-xs px-2 py-1 rounded-full bg-secondary">Live</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="grid md:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-card border border-white/10 rounded-xl p-6 hover:border-primary/40 transition-colors"
              data-testid={`feature-${i}`}
            >
              <span className="w-10 h-10 rounded-lg bg-secondary/40 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" />
              </span>
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 pb-24 grid lg:grid-cols-2 gap-10 items-center">
        <div className="rounded-2xl overflow-hidden border border-white/10">
          <img src={CAFE} alt="Cafe deals" className="w-full h-[380px] object-cover" />
        </div>
        <div>
          <span className="overline text-primary">How it works</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">Three steps to your marketing page</h2>
          <div className="mt-8 space-y-6">
            {[
              { icon: Store, t: "Register your business", d: "Add your name, logo, contact info and social links." },
              { icon: Tag, t: "List your offers & deals", d: "Organize with categories. Link each to a detail page or external URL." },
              { icon: Share2, t: "Share your page", d: "Get a clean public link customers can click through instantly." },
            ].map((s, i) => (
              <div key={s.t} className="flex gap-4" data-testid={`step-${i}`}>
                <span className="w-10 h-10 shrink-0 rounded-full border border-primary/40 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-primary" />
                </span>
                <div>
                  <h4 className="font-display font-semibold">{s.t}</h4>
                  <p className="text-sm text-muted-foreground">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/register"><Button className="mt-8 rounded-full bg-primary hover:bg-secondary" data-testid="howitworks-cta">Get started free</Button></Link>
        </div>
      </section>

      <Pricing />

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-primary" /> DealWisp
          </div>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="hover:text-white" data-testid="landing-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-white" data-testid="landing-privacy-link">Privacy</Link>
            <span>© {new Date().getFullYear()} DealWisp. Built for small businesses.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
